import { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { CheckSquare, X, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

/**
 * Barra y Modal para borrado masivo
 * 
 * @param {Object} props
 * @param {Array} props.selectedIds - IDs seleccionados
 * @param {Function} props.onClear - Función para limpiar selección
 * @param {Function} props.onSuccess - Callback después de eliminación exitosa
 * @param {String} props.endpoint - Endpoint base (ej: '/ipam/vlans')
 * @param {String} props.entityName - Nombre de la entidad (ej: 'VLAN', 'Subnet')
 */
const BulkDeleteBar = ({ 
  selectedIds = [], 
  onClear, 
  onSuccess,
  endpoint,
  entityName = 'registro'
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Si no hay selección, no mostrar nada
  if (selectedIds.length === 0) return null;

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`${endpoint}/bulk-delete`, { data: { ids: selectedIds } });
      toast.success(`${selectedIds.length} ${entityName}(s) eliminados exitosamente`);
      setShowModal(false);
      onClear();
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Barra de selección */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
        <div className="flex items-center gap-3 bg-destructive text-destructive-foreground px-4 py-2 rounded-lg shadow-lg">
          <CheckSquare className="h-5 w-5" />
          <span className="font-medium">{selectedIds.length} seleccionados</span>
          <div className="h-4 w-px bg-destructive-foreground/30" />
          <Button 
            size="sm" 
            variant="secondary"
            onClick={() => setShowModal(true)}
            className="h-7 bg-white/20 hover:bg-white/30 text-white"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Eliminar
          </Button>
          <Button 
            size="sm" 
            variant="ghost"
            onClick={onClear}
            className="h-7 text-destructive-foreground hover:text-destructive-foreground hover:bg-destructive-foreground/20"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Modal de confirmación */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Confirmar eliminación
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              ¿Estás seguro de que deseas eliminar <strong>{selectedIds.length} {entityName}(s)</strong>?
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? 'Eliminando...' : `Eliminar ${selectedIds.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkDeleteBar;
